import { useState } from 'react';
import { getIconByName } from '@/utils/iconMapping';
import { useRoleSwitcher } from '@/features/auth';
import useParentStore from '@/stores/parentStore';
import Button from '@/components/ui/Button';
import {
  filterParentNavigationItems,
  resolveParentNavigationHref,
} from '@/components/menu/parentNavigation';

const MAX_DOCK_SLOTS = 5;

// Custom dock styling — we do NOT use daisyUI's `.dock` class because its
// `> *` children styling + `.btn` wrapper combine into items that can't shrink
// and spread across wide (tablet) viewports.  Instead we constrain the rail to
// a sensible centered max-width and let items flex-share the row.
const DOCK_ITEM_BASE =
  'group flex min-w-0 basis-[72px] max-w-[96px] shrink flex-col items-center justify-center gap-1 px-1 py-1 rounded-lg transition-colors focus:outline-none';

/**
 * Mobile bottom-navigation dock with "More" overflow sheet.
 *
 * When the filtered top-level item count exceeds MAX_DOCK_SLOTS (5), the last
 * slot becomes a "More" button that opens a bottom sheet listing the overflow
 * items.  Every top-level menu item is therefore reachable — no silent hiding.
 *
 * @param {Object}   props
 * @param {Array}    props.menuItems  Full role-scoped menu list (hierarchical).
 * @param {Function} props.navigate   Router navigate function.
 * @param {Object}   props.location   Router location object ({ pathname }).
 */
const MobileDock = ({ menuItems, navigate, location }) => {
  const [showChildrenModal, setShowChildrenModal] = useState(null);
  const [showOverflowSheet, setShowOverflowSheet] = useState(false);
  const { currentRole } = useRoleSwitcher();
  const { linkedChildren, hasLoadedChildren } = useParentStore();

  if (!menuItems) return null;

  const availableMenuItems = filterParentNavigationItems(menuItems, {
    currentRole,
    linkedChildren,
  });

  // Filter to top-level visible items (no artificial slice cap)
  const visible = availableMenuItems.filter(
    item =>
      item.showInMenu !== false &&
      (item.depth === 0 || item.depth === undefined) &&
      !item.parentId
  );

  // Helper: substitute :studentId for parent role navigation
  const substituteRouteParams = (href) => {
    return resolveParentNavigationHref(href, {
      currentRole,
      linkedChildren,
      hasLoadedChildren,
    });
  };

  // Split visible items into dock slots and overflow
  let dockItems = visible;
  let overflowItems = [];
  if (visible.length > MAX_DOCK_SLOTS) {
    dockItems = visible.slice(0, MAX_DOCK_SLOTS - 1);
    overflowItems = visible.slice(MAX_DOCK_SLOTS - 1);
  }

  // Click handler for dock items (leaf → navigate, parent → children sheet)
  const handleItemClick = (item) => {
    if (item.children && item.children.length > 0) {
      setShowChildrenModal(item);
    } else {
      navigate(substituteRouteParams(item.href));
    }
  };

  // Click handler for child items inside the children sheet
  const handleChildClick = (child) => {
    navigate(substituteRouteParams(child.href));
    setShowChildrenModal(null);
  };

  // Click handler for overflow items inside the overflow sheet
  const handleOverflowItemClick = (item) => {
    setShowOverflowSheet(false);
    if (item.children && item.children.length > 0) {
      setShowChildrenModal(item);
    } else {
      navigate(substituteRouteParams(item.href));
    }
  };

  // More button is active when current pathname is inside any overflow item
  const isOverflowActive = overflowItems.some(item => {
    if (item.children && item.children.length > 0) {
      return item.children.some(child => location.pathname === child.href);
    }
    return location.pathname === item.href;
  });

  const renderItemContent = (item, isActive, labelOverride) => (
    <>
      {getIconByName(
        item.icon,
        `w-6 h-6 shrink-0 transition-colors ${
          isActive ? 'text-primary' : 'text-base-content/70 group-hover:text-base-content'
        }`
      )}
      <span
        className={`dock-label text-[11px] leading-none truncate max-w-full transition-colors ${
          isActive ? 'text-primary font-semibold' : 'text-base-content/70 group-hover:text-base-content'
        }`}
      >
        {labelOverride ?? item.label}
      </span>
      <span
        aria-hidden="true"
        className={`h-[2px] w-5 rounded-full bg-primary transition-opacity ${
          isActive ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </>
  );

  return (
    <>
      {/* Bottom dock — custom-styled so items share the rail tightly and the
          rail stays centered (max-w-md) on tablet widths instead of stretching. */}
      <nav
        className="fixed bottom-0 inset-x-0 z-50 border-t border-base-300 bg-base-100 lg:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="mx-auto flex h-16 w-full max-w-md items-stretch justify-center gap-1 px-2">
          {dockItems.map(item => {
            const hasChildren = item.children && item.children.length > 0;
            const isActive = hasChildren
              ? item.children.some(child => location.pathname === child.href)
              : location.pathname === item.href;

            return (
              <button
                key={item.id}
                type="button"
                className={`${DOCK_ITEM_BASE} ${isActive ? 'dock-active' : ''}`}
                onClick={() => handleItemClick(item)}
                aria-label={item.label}
              >
                {renderItemContent(item, isActive)}
              </button>
            );
          })}

          {overflowItems.length > 0 && (
            <button
              type="button"
              className={`${DOCK_ITEM_BASE} ${isOverflowActive ? 'dock-active' : ''}`}
              onClick={() => setShowOverflowSheet(true)}
              aria-label="More"
              data-testid="dock-more-button"
            >
              {renderItemContent({ icon: 'EllipsisHorizontalIcon' }, isOverflowActive, 'More')}
            </button>
          )}
        </div>
      </nav>

      {/* Children sheet — for dock items (and overflow items) that have children */}
      {showChildrenModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 lg:hidden flex items-end"
          onClick={() => setShowChildrenModal(null)}
        >
          <div
            className="bg-base-100 w-full rounded-t-2xl p-6 max-h-[70vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-6">
                {getIconByName(showChildrenModal.icon, 'w-5 h-5')}
                {showChildrenModal.label}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                className="btn-circle"
                onClick={() => setShowChildrenModal(null)}
                aria-label="Close"
              >
                {getIconByName('x-mark', 'w-4 h-4')}
              </Button>
            </div>
            <div className="space-y-2">
              {showChildrenModal.children.map(child => (
                <Button
                  key={child.id}
                  variant="ghost"
                  className="w-full justify-start gap-6"
                  onClick={() => handleChildClick(child)}
                >
                  {getIconByName(child.icon, 'w-5 h-5')}
                  {child.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Overflow sheet — lists items that didn't fit in dock slots */}
      {showOverflowSheet && (
        <div
          className="fixed inset-0 bg-black/50 z-50 lg:hidden flex items-end"
          onClick={() => setShowOverflowSheet(false)}
        >
          <div
            className="bg-base-100 w-full rounded-t-2xl p-6 max-h-[70vh] overflow-y-auto"
            data-testid="dock-more-sheet"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">More</h3>
              <Button
                variant="ghost"
                size="sm"
                className="btn-circle"
                onClick={() => setShowOverflowSheet(false)}
                aria-label="Close"
              >
                {getIconByName('x-mark', 'w-4 h-4')}
              </Button>
            </div>
            <div className="space-y-2">
              {overflowItems.map(item => (
                <Button
                  key={item.id}
                  variant="ghost"
                  className="w-full justify-start gap-6"
                  onClick={() => handleOverflowItemClick(item)}
                >
                  {getIconByName(item.icon, 'w-5 h-5')}
                  {item.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileDock;
